import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Wallet, Zap, Copy, Mail, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrencyExact } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RentTimeline from "@/components/RentTimeline";
import { toast } from "sonner";

const RECENT_CONTRACTS_SHOWN = 6;

export default function Payments() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [linkFor, setLinkFor] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);

  const { data: contracts = [] } = useQuery({ queryKey: ["contracts"], queryFn: async () => (await api.get("/contracts")).data });
  const { data: properties = [] } = useQuery({ queryKey: ["properties"], queryFn: async () => (await api.get("/properties")).data });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: async () => (await api.get("/tenants")).data });

  const propName = (id) => properties.find((p) => p.id === id)?.name || "—";
  const tenantName = (id) => tenants.find((x) => x.id === id)?.name || "—";
  const currentMonth = new Date().toISOString().slice(0, 7);
  const recentContracts = contracts.slice(0, RECENT_CONTRACTS_SHOWN);

  const createLink = useMutation({
    mutationFn: ({ id, sendEmail }) => api.post(`/rent-billing/contracts/${id}/payment-link`, { send_email: sendEmail }),
    onSuccess: (res) => {
      setGeneratedLink(res.data.url);
      if (res.data.email_sent) toast.success(t("payment_link_emailed"));
    },
    onError: (e) => { toast.error(apiErrorMessage(e.response?.data?.detail)); setLinkFor(null); },
  });

  const cancelAutoBilling = useMutation({
    mutationFn: (id) => api.post(`/rent-billing/contracts/${id}/cancel`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contracts"] }); toast.success("OK"); },
    onError: (e) => toast.error(apiErrorMessage(e.response?.data?.detail)),
  });

  const openLinkDialog = (id) => { setLinkFor(id); setGeneratedLink(null); createLink.mutate({ id, sendEmail: false }); };
  const copyLink = () => { navigator.clipboard.writeText(generatedLink); toast.success(t("link_copied")); };
  const emailLink = () => createLink.mutate({ id: linkFor, sendEmail: true });

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="font-display text-4xl">{t("nav_payments")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("payments_sub")}</p>
      </div>

      {contracts.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {recentContracts.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <Card className="p-6 rounded-sm border-border" data-testid={`payments-contract-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <h3 className="font-medium">{propName(c.property_id)} — {tenantName(c.tenant_id)}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{fmtCurrencyExact(c.rent_amount)}{t("billing_per_month")}</p>
                  </div>
                  <Badge className="rounded-sm bg-[hsl(var(--success))] text-white">{c.status}</Badge>
                </div>
                <RentTimeline contract={c} months={[currentMonth]} />
                <div className="flex items-center justify-between flex-wrap gap-2 mt-4">
                  <Link to={`/payments/${c.id}`} data-testid={`payments-detail-link-${c.id}`}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    {t("view_full_history")} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  {c.stripe_subscription_id ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="rounded-sm gap-1 text-[hsl(var(--success))] border-[hsl(var(--success))]">
                        <Zap className="h-3 w-3" /> {t("auto_billing_active")}
                      </Badge>
                      <Button variant="ghost" size="icon" title={t("auto_billing_cancel")}
                        onClick={() => cancelAutoBilling.mutate(c.id)} data-testid={`cancel-auto-billing-${c.id}`}
                        className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--destructive))]">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => openLinkDialog(c.id)}
                      data-testid={`enable-auto-billing-${c.id}`} className="rounded-sm gap-1.5">
                      <Zap className="h-3.5 w-3.5" /> {t("auto_billing_enable")}
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!linkFor} onOpenChange={(o) => { if (!o) setLinkFor(null); }}>
        <DialogContent className="bg-popover">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("payment_link_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("payment_link_desc")}</p>
          {createLink.isPending ? (
            <p className="text-sm text-muted-foreground py-4">…</p>
          ) : generatedLink && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2 p-2 rounded-sm border border-border bg-secondary/40">
                <code className="text-xs truncate flex-1">{generatedLink}</code>
                <Button variant="ghost" size="icon" onClick={copyLink} data-testid="copy-payment-link"
                  className="h-8 w-8 shrink-0"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <Button variant="outline" onClick={emailLink} disabled={createLink.isPending}
                data-testid="email-payment-link" className="rounded-sm gap-2 w-full">
                <Mail className="h-4 w-4" /> {t("payment_link_send_email")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
